import { useEffect, useRef, useState } from 'react';

import { HERO_CELL, HERO_SIZE, HERO_URL, heroFrame } from '@/game/data/heroAtlas';
import { heroContact } from '@/game/data/heroContact';
import { useSession } from '@/store/session';

/**
 * The player in the cave — mock panels 3, 4 and 9.
 *
 * Three poses, because the mock uses three: two ends of a stride seen from BEHIND
 * (torch raised, facing into the dark) and a side view while swinging, since you
 * cannot read an axe arc from directly behind.
 *
 * These are Blender renders of the character the player actually chose, from
 * `tools/blender/build_characters.py --poses cave`. Before this it was a generic
 * inline-SVG miner, so picking Luna or Thor changed the hero screen and nothing
 * else — the one screen where you spend the whole game showed someone else.
 *
 * The SVG is kept as a real fallback, not decoration: an atlas that failed to
 * load, or a character with no rendered pose yet, still has to leave a player
 * visible in the cave.
 *
 * Deliberately small on screen. The mock makes the miner a silhouette against the
 * dark, not the subject — the cave is the subject.
 */
/**
 * The torch, applied to the character render.
 *
 * The flame sits left of centre (`TORCH_U`) and the miner stands in the middle,
 * so the light arrives from the left: warm rim on that side, cast shadow to the
 * right. Same vocabulary as `RockField.rockShading` — if the rocks are lit from
 * the flame and the person holding it is not, the person is the thing that looks
 * wrong.
 */
const MINER_LIT = [
  'brightness(.92) contrast(1.12) saturate(.98) sepia(.14) hue-rotate(-8deg)',
  'drop-shadow(5px 4px 7px rgba(0,0,0,.7))',
  'drop-shadow(-3px 0 5px rgba(255,186,102,.34))',
].join(' ');

export function MinerBack({ walking, swinging }: { walking: boolean; swinging: boolean }) {
  const explorer = useSession((s) => s.characters.explorer);
  const hero = explorer?.key ?? '';

  /* The stride phase lives in a REF, not in state, and that is the whole fix.
   *
   * `walking` is true for one step only (CONFIG.stepMs, 420 ms) and the old timer
   * was created per step at 210 ms — so it fired at most once before being torn
   * down, and often not at all. The frame barely changed and consecutive steps
   * restarted from the same foot, which is why the character did not read as
   * walking. A ref survives the remount, so step two continues the cycle step one
   * started.
   *
   * Swapped, never cross-faded: two-frame walk cycles have always worked by
   * substitution, and blending them reads as a ghost. */
  const phase = useRef(0);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!walking) return;
    const id = setInterval(() => { phase.current += 1; tick((n) => n + 1); }, 130);
    return () => clearInterval(id);
  }, [walking]);
  const strideB = phase.current % 2 === 1;

  const pose = swinging ? 'swing' : (walking && strideB ? 'walk_b' : 'walk_a');
  const frameKey = `poses/character_${hero}_${pose}`;
  const frame = hero ? heroFrame(frameKey) : null;
  const contact = hero ? heroContact(frameKey) : null;

  if (frame) {
    /*
     * PERCENTAGE background sizing, not pixels.
     *
     * GearIcon can multiply by a known `size` because the shop asks for 48px.
     * Corridor sizes this slot as a percentage of the viewport, so there is no
     * pixel number to scale by — the sheet has to be positioned in units that
     * resolve at any size. With a uniform grid, `background-size: cols00%` and a
     * position of `x / (sheetW - cell)` does exactly that.
     *
     * The inner box is forced square (`aspectRatio`), because percentage
     * background-size stretches to the element's own box and Corridor's slot is
     * taller than it is wide — the player would render subtly squashed.
     */
    const cols = HERO_SIZE.width / HERO_CELL;
    const rows = HERO_SIZE.height / HERO_CELL;
    const shiftX = (frame.x / HERO_CELL) * 100;
    const shiftY = (frame.y / HERO_CELL) * 100;

    return (
      <div className="grid h-full w-full place-items-end justify-center">
        {/* The square the sprite actually occupies. Corridor's slot is 18% wide
            and 26% tall, so the sprite — forced square off its height — is more
            than twice the slot's width and overflows it evenly. Anything meant to
            line up with the character therefore has to be positioned against THIS
            box, not the slot: the contact cue was first hung off the slot and
            landed a boot-width to the right of the boots. */}
        <div
          className="relative h-full select-none"
          style={{
            aspectRatio: '1 / 1',
            /* The body's rise and fall is driven by the SAME stride phase as the
               frame swap, not by a CSS animation, and that is a fix rather than a
               simplification.
             *
             * It was `walkBob .52s infinite`, whose two peaks land at 130 ms and
             * 390 ms — correctly in step with a 130 ms frame flip, but only for the
             * first step. `walking` goes false after CONFIG.stepMs (420 ms), so the
             * animation restarts from 0 on the next step while `phase` deliberately
             * persists across steps to keep the walk cycle continuous. After an odd
             * number of frames the two were inverted: the body rose on the planted
             * foot and sank on the lifted one, which reads as a limp.
             *
             * Reading both from `strideB` makes desync unrepresentable. The
             * transition keeps it smooth rather than stepped, and the global
             * prefers-reduced-motion rule in tokens.css caps transition-duration,
             * so this degrades the same way the animation did. */
            transform: `translate3d(0, ${walking && strideB ? -4 : 0}px, 0)`,
            transition: 'transform 130ms ease-in-out',
            transformOrigin: 'bottom center',
          }}
          role="img"
          aria-label={explorer?.name ?? 'Your explorer'}
        >
          {/*
            * Ground contact, and it is LIGHT rather than shadow.
            *
            * The miner stood on pure black with nothing under the boots, so a
            * character rendered on a studio floor read as pasted over the cave
            * rather than standing in it. A dark ellipse is the usual fix and does
            * nothing here — the cave floor is already near-black, so the cue has
            * to be the torch catching the ground the boots are on. Same
            * reasoning, and the same failure, as the floor rubble in RockField.
            *
            * Placed PER FRAME from measured pixels (`heroContact`), not at a fixed
            * `left: 50%`. It was one hand-tuned constant, and measuring all 18
            * cave-visible frames showed why that cannot work: the feet's horizontal
            * centre swings 44.06% -> 52.86% between walk_a and walk_b, because that
            * swing is what a stride IS. A fixed centre therefore sat ~5% of the cell
            * off the boots on every walk_a frame and drifted back on walk_b — the
            * feet moved and the light stayed still, which is the exact "floating over
            * a lit patch" read the cue exists to prevent. The boot line also varies
            * by character (9.77%-12.89%; Rocky stands lowest), so no single vertical
            * value was right for all six either.
            *
            * The pool is kept wider than the feet — light spreads on the floor — and
            * so it widens as the stride opens, which is also what really happens.
            * Transitioned on the same 130 ms as the frame flip so it travels with the
            * feet instead of snapping between them.
            */}
          <span
            aria-hidden
            className="pointer-events-none absolute h-[4.5%] -translate-x-1/2 rounded-[50%]"
            style={{
              bottom: `${contact?.bottomPct ?? 10.5}%`,
              left: `${contact?.centerPct ?? 50}%`,
              width: `${Math.min(40, Math.max(20, (contact?.widthPct ?? 16) + 12))}%`,
              transition: 'bottom 130ms ease-in-out, left 130ms ease-in-out,'
                + ' width 130ms ease-in-out',
              background:
                'radial-gradient(ellipse at center, rgba(255,186,102,.26) 0%,' +
                ' rgba(90,54,20,.18) 50%, rgba(0,0,0,0) 76%)',
            }}
          />
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            /* Lit by the torch the character is holding, and shadowed away from
               it. Without this the miner was the most saturated, most neutral
               thing on screen — every other layer had been put under the light in
               bf6ad29 and this one was missed. The warm pass is a rim on the
               torch side, which is what rescues a dark silhouette against a dark
               wall; the dark pass falls to the right, away from the flame at
               TORCH_U. */
            filter: MINER_LIT,
            WebkitFilter: MINER_LIT,
            willChange: 'transform',
            transform: 'translate3d(0,0,0)',
            WebkitTransform: 'translate3d(0,0,0)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          <img
            src={HERO_URL}
            alt=""
            aria-hidden
            style={{
              position: 'absolute',
              width: `${cols * 100}%`,
              height: `${rows * 100}%`,
              maxWidth: 'none',
              left: `-${shiftX}%`,
              top: `-${shiftY}%`,
              /* Deliberately NOT `-webkit-optimize-contrast`, which was here
                 before. The atlas cell is 256px now (was 128px — see
                 heroAtlas.ts), so a 154 CSS px slot on a 3× phone is roughly a
                 1.8× upscale rather than 3.6×, but it is still an upscale, and
                 that hint still pushes engines toward a sharper,
                 nearest-neighbour-ish filter that turns soft-render blur into
                 blocky blur. Smooth interpolation stays the better default. */
              willChange: 'transform',
              transform: 'translate3d(0,0,0)',
              WebkitTransform: 'translate3d(0,0,0)',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
            }}
            draggable={false}
          />
        </div>
        </div>
      </div>
    );
  }

  if (swinging) return <MinerSide />;

  return (
    <svg viewBox="0 0 64 96" className="h-full w-full overflow-visible" aria-hidden>
      {/* torch arm, raised to the right — the light source the vignette follows */}
      <g className={walking ? 'origin-[46px_44px] animate-[armSway_.42s_ease-in-out]' : ''}>
        <rect x="44" y="30" width="4" height="26" rx="1.5" fill="#6B4A2C" />
        <g className="origin-[46px_28px] animate-[flicker_.22s_steps(3)_infinite]">
          <path d="M46 8 q9 11 6 18 q-3 5 -6 5 q-3 0 -6 -5 q-3 -7 6 -18 z" fill="var(--torch-deep)" />
          <path d="M46 14 q5.5 8 3.5 13 q-1.5 3.5 -3.5 3.5 q-2 0 -3.5 -3.5 q-2 -5 3.5 -13 z" fill="var(--torch-warm)" />
          <path d="M46 20 q2.5 5 1.5 8 q-.7 2 -1.5 2 q-.8 0 -1.5 -2 q-1 -3 1.5 -8 z" fill="var(--torch-core)" />
        </g>
      </g>

      {/* legs — alternating stride while walking */}
      <g className={walking ? 'origin-[26px_74px] animate-[stride_.42s_ease-in-out]' : ''}>
        <rect x="22" y="66" width="9" height="24" rx="3.5" fill="#22383F" />
        <rect x="20" y="86" width="14" height="7" rx="2" fill="#120E0C" />
      </g>
      <g className={walking ? 'origin-[38px_74px] animate-[strideAlt_.42s_ease-in-out]' : ''}>
        <rect x="33" y="66" width="9" height="24" rx="3.5" fill="#33606C" />
        <rect x="31" y="86" width="14" height="7" rx="2" fill="#1B1512" />
      </g>

      {/* torso + pack, seen from behind */}
      <rect x="20" y="34" width="24" height="34" rx="9" fill="#2C505A" />
      <rect x="23" y="38" width="18" height="20" rx="5" fill="#6B4A2A" />
      <rect x="23" y="44" width="18" height="3" rx="1.5" fill="#4A3320" />

      {/* head + helmet — the helmet lamp is the small warm dot */}
      <rect x="24" y="16" width="16" height="17" rx="6" fill="#D9A87C" />
      <path d="M22 18 q1.5 -13 10 -13 q8.5 0 10 13 z" fill="#F0A93F" />
      <rect x="20" y="16" width="24" height="4.5" rx="2" fill="#D9861F" />
      <circle cx="32" cy="11" r="2.2" fill="var(--torch-core)" opacity=".9" />
    </svg>
  );
}

/** Side view for the swing — you cannot read an axe arc from behind. */
function MinerSide() {
  return (
    <svg viewBox="0 0 64 96" className="h-full w-full overflow-visible" aria-hidden>
      <g className="origin-[30px_44px] animate-[chop_.34s_cubic-bezier(.3,1.4,.4,1)]">
        <rect x="28" y="26" width="4" height="30" rx="1.5" fill="#6B4A2C" />
        <path d="M18 28 q11 -8 22 -1.5 l-2 3.5 q-10 -5 -18.5 1.5 z" fill="#8B98A5" />
        <path d="M18 28 q11 -8 22 -1.5 l-1 1.7 q-10.5 -5.5 -20 1.5 z" fill="#C3CDD6" />
      </g>
      <rect x="24" y="66" width="9" height="24" rx="3.5" fill="#22383F" />
      <rect x="34" y="66" width="9" height="24" rx="3.5" fill="#33606C" />
      <rect x="22" y="86" width="24" height="7" rx="2" fill="#120E0C" />
      <rect x="24" y="34" width="22" height="34" rx="9" fill="#2C505A" />
      <rect x="30" y="16" width="16" height="17" rx="6" fill="#EBC094" />
      <path d="M28 18 q1.5 -13 10 -13 q8.5 0 10 13 z" fill="#F0A93F" />
      <rect x="26" y="16" width="24" height="4.5" rx="2" fill="#D9861F" />
      <circle cx="44" cy="24" r="1.8" fill="#3A2A20" />
    </svg>
  );
}
